import { Component, Input, OnInit } from "@angular/core";
import { PAOperation } from "../classes/operation";
import { PAStore } from "../classes/store";
import { PAUtil } from "../classes/util";
import { PARegistry } from "../singletons/pa-registry";
import { TicketService } from "src/app/_services";

import * as mapboxgl from "mapbox-gl";
import MapboxGeocoder from "@mapbox/mapbox-gl-geocoder";

/**
 * Einordnung eines Auftrags ohne Koordinaten für die Anzeige: ungeplant oder
 * bereits in der Tour eines Technikers.
 */
interface OperationClassification {
  operation: PAOperation;
  planned: boolean;
  technicianName: string;
  dateString: string;
}

@Component({
  selector: "hawk-insufficient-data-popup",
  templateUrl: "./insufficient-data-popup.component.html",
  styleUrls: ["./insufficient-data-popup.component.scss"],
  standalone: true,
  imports: [],
})
export class InsufficientDataPopupComponent implements OnInit {
  @Input() operationsWithoutLngLat: PAOperation[] = [];
  @Input() mapboxAccessToken: string;

  selectedInstance: PAOperation;
  geocoder: MapboxGeocoder;
  searchedAddress: {
    address_latitude: number;
    address_longitude: number;
    address_street_no?: string;
    address_street?: string;
    address_zip_code?: string;
    address_city?: string;
    address_country?: string;
  };
  /**
   * Bewusst ein Getter und kein gepflegtes Feld: Das Eingabe-Array wird von
   * PADataControl per push befüllt, seine Referenz ändert sich dabei nie.
   * ngOnChanges feuert also nicht, und ein zwischengespeicherter Wert bliebe
   * auf dem Stand vom Erzeugen der Komponente stehen — der Sammel-Knopf
   * (ab zwei Aufträgen) wäre dann nie zu sehen.
   */
  get insufficientInstanceAmount(): number {
    return this.operationsWithoutLngLat.length;
  }

  // Zwischenschritt vor dem Sammelausschluss: solange gesetzt, zeigt das Popup
  // die Aufstellung statt der Auftragsliste.
  bulkExclusionSummary: { unplanned: OperationClassification[]; planned: OperationClassification[] } = null;

  constructor(private ticketService: TicketService) {}

  ngOnInit(): void {
    this.geocoder = new MapboxGeocoder({
      accessToken: this.mapboxAccessToken,
      mapboxgl: mapboxgl,
      placeholder: "Adresse suchen...",
    });
    this.initGeocoder();
    this.selectNextInstance();
  }

  private selectNextInstance() {
    this.selectedInstance = this.operationsWithoutLngLat.length ? this.operationsWithoutLngLat[0] : null;
  }

  selectInstance(operation: PAOperation): void {
    this.selectedInstance = operation;
    this.resetSearchedAddress();
  }

  async initGeocoder(): Promise<void> {
    await PAUtil.sleepUntilConditionBreaks(() => {
      return !document.getElementById("ins_data_geocoder");
    });
    this.geocoder.addTo(document.getElementById("ins_data_geocoder"));
    const insDataObj = this;
    this.geocoder.on("result", function (event) {
      insDataObj.parseSearchedAddress(event);
    });
  }

  parseSearchedAddress(event: {
    result: {
      center: number[];
      id: string;
      text: string;
      mapbox_id: string;
      address: string;
      place_name: string;
      context: { id: string; mapbox_id: string; text: string }[];
    };
  }): void {
    const searched_address = {
      address_latitude: null,
      address_longitude: null,
      address_street_no: null,
      address_street: null,
      address_zip_code: null,
      address_city: null,
      address_country: null,
    };
    searched_address.address_latitude = event.result.center[1];
    searched_address.address_longitude = event.result.center[0];
    const full_context = event.result.context.concat({
      id: event.result.id,
      text: event.result.text + (event.result.address ? " " + event.result.address : ""),
      mapbox_id: event.result.mapbox_id,
    });
    const possible_country = full_context.filter((context) => context.id.startsWith("country."));
    const possible_place = full_context.filter((context) => context.id.startsWith("place."));
    if (possible_place.length) {
      searched_address.address_city = possible_place[0].text;
    }
    const possible_post_code = full_context.filter((context) => context.id.startsWith("postcode."));
    if (possible_post_code.length) {
      searched_address.address_zip_code = possible_post_code[0].text;
    }
    const possible_address = full_context.filter((context) => context.id.startsWith("address."));
    if (possible_address.length) {
      searched_address.address_street = possible_address[0].text;
    }
    if (possible_country[0].text) {
      searched_address.address_country = possible_country[0].text;
    }
    this.searchedAddress = searched_address;
  }

  resetSearchedAddress(): void {
    this.searchedAddress = null;
    this.geocoder?.clear();
  }

  selectedInstanceIsReadyToUpdate(): boolean {
    return !!(this.searchedAddress && this.searchedAddress.address_latitude && this.searchedAddress.address_longitude);
  }

  async updateSelectedInstance(): Promise<void> {
    if (this.searchedAddress.address_latitude && this.searchedAddress.address_longitude) {
      await this.updateOperation(this.selectedInstance);
    }
    this.resetSearchedAddress();
    this.selectNextInstance();
  }

  async updateOperation(operation: PAOperation): Promise<void> {
    operation.ticket.coordinates.latitude =
      operation.ticket.coordinates.latitude =
      operation.ticket.client.coordinates.latitude =
      operation.ticket.client.location.coordinates.latitude =
        this.searchedAddress.address_latitude;
    operation.ticket.coordinates.longitude =
      operation.ticket.coordinates.longitude =
      operation.ticket.client.coordinates.longitude =
      operation.ticket.client.location.coordinates.longitude =
        this.searchedAddress.address_longitude;
    this.searchedAddress.address_city
      ? (operation.ticket.address_city = operation.ticket.address_city = this.searchedAddress.address_city)
      : {};
    this.searchedAddress.address_street ? (operation.ticket.address_street = this.searchedAddress.address_street) : {};
    this.searchedAddress.address_street_no
      ? (operation.ticket.address_street_no = this.searchedAddress.address_street_no)
      : {};
    this.searchedAddress.address_zip_code
      ? (operation.ticket.address_zip = operation.ticket.address_zip = this.searchedAddress.address_zip_code)
      : {};

    // Der Auftrag ist jetzt routbar und wird nachgetragen: Beim Laden wurde er
    // mangels Koordinaten weder in einen Store noch in eine Tour aufgenommen.
    // insertOperation deckt den Store ab (auch für ungeplante Aufträge),
    // insertOperationsInTechnicianDateAndStore hängt ihn bei eingeplanten
    // zusätzlich in die Tour und rechnet deren Route neu.
    PAStore.insertOperation(operation);
    await PAOperation.insertOperationsInTechnicianDateAndStore([operation]);

    // Ungeplante Aufträge blendet getUnassignedOperations ohne Koordinaten
    // aus — der Pool muss nach dem Nachtragen neu gebildet werden, sonst
    // taucht der Auftrag in den Listen nicht wieder auf.
    PARegistry.filterControl.updateUnassignedOperations();

    PAUtil.removeElementFromList(this.operationsWithoutLngLat, operation);
    this.updateOperationAddressInDB(operation, this.searchedAddress);
  }

  updateOperationAddressInDB(
    operation: PAOperation,
    address_data: {
      address_latitude: number;
      address_longitude: number;
      address_street_no?: string;
      address_street?: string;
      address_zip?: string;
      address_city?: string;
      address_country?: string;
    },
  ) {
    this.ticketService.updateTicket(operation.ticket.id.toString(), { ticket: address_data }).subscribe(
      (_) => {},
      (error) => {
        console.log(error);
      },
    );
  }

  /**
   * Ordnet einen Auftrag für die Anzeige ein. Bei eingeplanten Aufträgen wird
   * zusätzlich Techniker und Tag genannt, damit der Dispatcher weiß, in welcher
   * Tour die Lücke entsteht.
   */
  classify(operation: PAOperation): OperationClassification {
    const technician = operation.isUnassigned() ? null : operation.getTechnician();
    const planned_date = operation.getPlannedDate();

    return {
      operation: operation,
      planned: !!technician,
      technicianName: technician ? `${technician.firstname} ${technician.lastname}` : "",
      dateString: planned_date
        ? PARegistry.timeControl.dateToDatestring(new Date(planned_date), false, false, ".")
        : "",
    };
  }

  classifiedOperations(): OperationClassification[] {
    return this.operationsWithoutLngLat.map((operation) => this.classify(operation));
  }

  getOperationName(operation: PAOperation): string {
    return `Ticket: ID-${operation.ticket.id}${operation.ticket.address_company ? " - " + operation.ticket.address_company : ""}`;
  }

  /**
   * Nimmt einen einzelnen Auftrag aus der Sitzung. Die Einordnung steht in der
   * Zeile daneben, deshalb ohne Rückfrage — anders als beim Sammelausschluss.
   */
  async excludeOperation(operation: PAOperation): Promise<void> {
    await PARegistry.dataControl.excludeOperationFromSession(operation);
    PAUtil.removeElementFromList(this.operationsWithoutLngLat, operation);
    if (this.selectedInstance == operation) this.selectNextInstance();
  }

  /**
   * Öffnet die Aufstellung vor dem Sammelausschluss: wie viele Aufträge
   * ungeplant sind und welche Touren betroffen wären.
   */
  startBulkExclusion(): void {
    const classified = this.classifiedOperations();
    this.bulkExclusionSummary = {
      unplanned: classified.filter((entry) => !entry.planned),
      planned: classified.filter((entry) => entry.planned),
    };
  }

  cancelBulkExclusion(): void {
    this.bulkExclusionSummary = null;
  }

  async confirmBulkExclusion(): Promise<void> {
    // Über eine Kopie laufen — excludeOperationFromSession kürzt die Liste,
    // über die sonst gerade iteriert würde.
    for (const operation of [...this.operationsWithoutLngLat]) {
      await this.excludeOperation(operation);
    }
    this.bulkExclusionSummary = null;
  }
}
